/**
 * Preview shortcuts — jump straight to a screen without answering 12 questions.
 *
 *   ?preview=results          the full NDSS report (default SPL-2)
 *   ?preview=score            the ungated score reveal
 *   ?preview=form             the contact form
 *   ?preview=q7               question 7
 *   &spl=0|1|2|3|4            pick the posture level to land on
 *   &answers=211122121132     exact option indices, 12 digits, 0-3
 *
 * DEV ONLY. `import.meta.env.DEV` is statically false in a production build, so
 * the whole module tree-shakes out of the bundle. That matters: `results` sits
 * behind the lead-capture gate, and a URL that skips the gate in production
 * would give away the thing the form exists to earn.
 */

import { QUESTIONS } from '@shared/config.js';
import type { OptionIndex } from '@shared/types.js';

export type PreviewStep = 'landing' | 'question' | 'score' | 'form' | 'results';

export interface Preview {
  step: PreviewStep;
  questionIndex: number;
  answers: OptionIndex[];
}

const twelve = (v: OptionIndex): OptionIndex[] =>
  Array.from({ length: QUESTIONS.length }, () => v);

/**
 * Answer sets that land on each posture level. Verified in preview.test.ts —
 * if the question set or weights change and a preset drifts to another SPL,
 * that test fails rather than the preview quietly lying.
 */
export const SPL_PRESETS: Record<0 | 1 | 2 | 3 | 4, OptionIndex[]> = {
  0: twelve(0), // -> 0/100
  1: twelve(1), // -> 27/100
  2: [2, 1, 1, 1, 2, 2, 1, 2, 1, 1, 3, 2], // -> 54/100
  3: twelve(2), // -> 73/100
  4: twelve(3), // -> 98/100
};

function parseAnswers(raw: string | null): OptionIndex[] | null {
  if (!raw) return null;
  const digits = raw.trim();
  if (digits.length !== QUESTIONS.length || !/^[0-3]+$/.test(digits)) return null;
  return [...digits].map((d) => Number(d) as OptionIndex);
}

export function readPreview(search = window.location.search): Preview | null {
  if (!import.meta.env.DEV) return null;

  const params = new URLSearchParams(search);
  const target = params.get('preview');
  if (!target) return null;

  const splParam = params.get('spl');
  const spl = splParam !== null && /^[0-4]$/.test(splParam)
    ? (Number(splParam) as 0 | 1 | 2 | 3 | 4)
    : 2;

  const answers = parseAnswers(params.get('answers')) ?? SPL_PRESETS[spl];

  // ?preview=q7 -> question 7 (1-based for humans, 0-based internally)
  const question = /^q(\d{1,2})$/i.exec(target);
  if (question) {
    const n = Math.min(Math.max(Number(question[1]), 1), QUESTIONS.length);
    return { step: 'question', questionIndex: n - 1, answers };
  }

  if (target === 'results' || target === 'score' || target === 'form') {
    return { step: target, questionIndex: QUESTIONS.length - 1, answers };
  }

  return null;
}
